import { useState } from 'react'

import type { FilaCartera, MedioDePago } from '@/lib/supabase/tipos-bd'
import { fecha as fechaCorta, pesos } from '@/shared/utilidades/formato'

import { hoy } from '@/modules/tablero/semana'
import { useRecibosAWo, useRegistrarPago } from './useCartera'
import estilos from './PaginaCartera.module.css'

/**
 * ANOTAR UN PAGO DE UNA CUENTA.
 *
 * Trae el saldo puesto —lo más común es que paguen todo— y la fecha de hoy,
 * que se cambia si el pago llegó otro día. La base no deja pagar más que el
 * saldo ni poner una fecha futura; el mensaje, si lo hay, sale tal cual.
 *
 * A QUÉ FACTURA (0293). La cartera de las FEV es la de World Office: un pago
 * a una FEV es un recibo de caja allá, y la app se lo pide. Lo que no es FEV
 * —una cotización sin IVA, una COT sin facturar— no tiene en WO a qué
 * abonarse y se anota solo aquí. Mientras el recibo automático esté apagado
 * (configuracion.recibos_a_wo), los pagos de una FEV se hacen en WO y llegan
 * solos.
 */
const MEDIOS: { valor: MedioDePago; texto: string; aWo: boolean }[] = [
  { valor: 'transferencia', texto: 'Transferencia', aWo: true },
  { valor: 'consignacion', texto: 'Consignación', aWo: true },
  { valor: 'efectivo', texto: 'Efectivo', aWo: true },
  { valor: 'tarjeta', texto: 'Tarjeta', aWo: true },
  { valor: 'cheque', texto: 'Cheque', aWo: false },
  { valor: 'otro', texto: 'Otro', aWo: false },
]

/** Lo que no es FEV, como opción del selector. */
const SIN_FEV = 'cot'

export function AnotarPago({ cuenta, onListo }: { cuenta: FilaCartera; onListo: () => void }) {
  const registrar = useRegistrarPago()
  const { data: recibosAWo = false } = useRecibosAWo()

  // Las FEV que deben, de la que vence primero a la última; y lo que se cobra
  // sin FEV: el saldo de la cuenta menos lo que deben sus FEV.
  const fevQueDeben = (cuenta.fev ?? [])
    .filter((f) => !f.contado && Number(f.saldo) > 0)
    .sort((a, b) => (a.vence ?? a.fecha ?? '').localeCompare(b.vence ?? b.fecha ?? ''))
  const saldoSinFev = Math.max(
    0,
    Number(cuenta.saldo) - fevQueDeben.reduce((t, f) => t + Number(f.saldo), 0),
  )
  const destinos = [
    ...fevQueDeben.map((f) => ({
      clave: String(f.wo_id_asiento),
      texto: `${f.numero} · debe ${pesos(Number(f.saldo))}${
        f.vence ? ` · ${f.vence < hoy() ? 'venció' : 'vence'} ${fechaCorta(f.vence)}` : ''
      }`,
      saldo: Number(f.saldo),
      asiento: f.wo_id_asiento as number | null,
    })),
    ...(saldoSinFev > 0 || fevQueDeben.length === 0
      ? [
          {
            clave: SIN_FEV,
            texto: `Lo que se cobra con la cotización · ${pesos(saldoSinFev)}`,
            saldo: saldoSinFev,
            asiento: null,
          },
        ]
      : []),
  ]

  const [destino, setDestino] = useState(destinos[0]?.clave ?? SIN_FEV)
  const elegido = destinos.find((d) => d.clave === destino) ?? destinos[0]
  const aFev = elegido?.asiento != null

  const [valor, setValor] = useState(String(Math.round(elegido?.saldo ?? 0)))
  const [fecha, setFecha] = useState(hoy())
  const [medio, setMedio] = useState<MedioDePago>('transferencia')
  const [referencia, setReferencia] = useState('')
  const [nota, setNota] = useState('')
  const [error, setError] = useState<string | null>(null)

  const numero = Number(valor.replace(/\D/g, ''))
  const medios = aFev ? MEDIOS.filter((m) => m.aWo) : MEDIOS
  // A una FEV con el recibo apagado no se anota aquí: se hace en WO.
  const enWo = aFev && !recibosAWo

  function cambiarDestino(clave: string) {
    setDestino(clave)
    const d = destinos.find((x) => x.clave === clave)
    setValor(String(Math.round(d?.saldo ?? 0)))
    if (d?.asiento != null && !MEDIOS.find((m) => m.valor === medio)?.aWo) {
      setMedio('transferencia')
    }
    setError(null)
  }

  async function guardar() {
    setError(null)
    try {
      await registrar.mutateAsync({
        ordenServicioId: cuenta.orden_servicio_id,
        valor: numero,
        fecha,
        medio,
        referencia,
        nota,
        woIdAsientoFactura: elegido?.asiento ?? null,
      })
      onListo()
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No se pudo anotar el pago.')
    }
  }

  return (
    <div className={estilos.formulario}>
      {destinos.length > 1 && (
        <label className={`${estilos.campo} ${estilos.campoEntero}`}>
          <span>¿A qué factura va?</span>
          <select value={destino} onChange={(e) => cambiarDestino(e.target.value)}>
            {destinos.map((d) => (
              <option key={d.clave} value={d.clave}>
                {d.texto}
              </option>
            ))}
          </select>
        </label>
      )}

      {enWo ? (
        <p className={estilos.pagoEnWo}>
          <b>
            {destinos.length === 1 && elegido ? `${elegido.texto}. ` : ''}Este pago se registra en
            World Office,
          </b>{' '}
          con su recibo de caja a la factura. En unos segundos aparece aquí solo, con el número del
          recibo, y el saldo baja.
        </p>
      ) : (
        <>
          <label className={estilos.campo}>
            <span>Valor</span>
            <input
              inputMode="numeric"
              value={numero ? numero.toLocaleString('es-CO') : ''}
              onChange={(e) => setValor(e.target.value)}
              aria-label="Valor del pago"
              autoFocus
            />
            <small>
              {destinos.length === 1 && elegido?.asiento != null
                ? `${elegido.texto}`
                : `Saldo: ${pesos(elegido?.saldo ?? Number(cuenta.saldo))}`}
            </small>
          </label>
          <label className={estilos.campo}>
            <span>Fecha del pago</span>
            <input
              type="date"
              value={fecha}
              max={hoy()}
              onChange={(e) => setFecha(e.target.value)}
            />
          </label>
          <label className={estilos.campo}>
            <span>Medio</span>
            <select value={medio} onChange={(e) => setMedio(e.target.value as MedioDePago)}>
              {medios.map((m) => (
                <option key={m.valor} value={m.valor}>
                  {m.texto}
                </option>
              ))}
            </select>
          </label>
          <label className={estilos.campo}>
            <span>Referencia</span>
            <input
              value={referencia}
              onChange={(e) => setReferencia(e.target.value)}
              placeholder="N.º de transferencia, banco…"
            />
          </label>
          <label className={`${estilos.campo} ${estilos.campoAncho}`}>
            <span>Nota (opcional)</span>
            <input
              value={nota}
              onChange={(e) => setNota(e.target.value)}
              placeholder="Abono, saldo…"
            />
          </label>
          {aFev && (
            <p className={estilos.pagoAviso}>
              Al anotarlo se crea el recibo de caja en World Office, abonado a esta factura.
            </p>
          )}
        </>
      )}

      {error && <p className={estilos.error}>{error}</p>}

      {/* Para hacerlo en WO no hay nada que guardar: se cierra con el botón de la tarjeta. */}
      {!enWo && (
        <div className={estilos.botones}>
          <button type="button" className={estilos.secundario} onClick={onListo}>
            Cancelar
          </button>
          <button
            type="button"
            className={estilos.principal}
            onClick={() => void guardar()}
            disabled={!numero || registrar.isPending}
          >
            {registrar.isPending ? 'Guardando…' : `Anotar ${numero ? pesos(numero) : 'pago'}`}
          </button>
        </div>
      )}
    </div>
  )
}
